import {animate, state, style, transition, trigger} from '@angular/animations'
import {ChangeDetectionStrategy, Component, DestroyRef, NgZone, OnDestroy, inject, signal} from '@angular/core'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {ToastNotificationDisableTimeOut, ToastNotificationStatusValue} from './config/enums/toast.enum'
import {IndividualToastNotificationConfig} from './config/interfaces/toast.interface'
import {ToastNotificationPackage} from './config/toast-config'
import {ToastNotificationService} from './toast-notification.service'

@Component({
  selector: 'app-toast-notification-component',
  templateUrl: './toast-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[@flyInOut]': 'animationState',
    '(@flyInOut.start)': 'onAnimationStart($event)',
    '[style.display]': 'displayStyle()',
    '[class]': 'toastClasses()',
    '(click)': 'tapToast()',
    '(mouseleave)': 'delayedHideToast()',
    '(mouseenter)': 'stickAround()',
  },
  animations: [
    trigger('flyInOut', [
      state('inactive', style({opacity: 0})),
      state('active', style({opacity: 1})),
      state('removed', style({opacity: 0})),
      // Entrada inicial
      transition('void => inactive', animate('0ms')),
      // Activación (la transición más importante)
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      // Desactivación
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
      // Salidas directas
      transition('inactive => void', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => void', animate('{{ easeTime }}ms {{ easing }}')),
      transition('removed => void', animate('0ms')),
    ]),
  ],
})
export class ToastNotificationComponent<ConfigPayload = any> implements OnDestroy {
  /**
   * Controls animation - using signal for reactivity
   */
  animationState = signal<{
    value: ToastNotificationStatusValue
    params: {easeTime: number | string; easing: string}
  }>({
    value: ToastNotificationStatusValue.INACTIVE,
    params: {easeTime: 300, easing: 'ease-in'},
  })

  /**
   * Getter for backward compatibility
   */
  get state() {
    return this.animationState()
  }

  /**
   * Setter for backward compatibility
   */
  set state(newState: {value: ToastNotificationStatusValue; params: {easeTime: number | string; easing: string}}) {
    this.animationState.set(newState)
  }

  /**
   * Hides component when waiting to be displayed
   */
  displayStyle = signal<string | null>('none')

  /**
   * A combination of toast type and options.toastClass
   */
  toastClasses = signal('')
  message = signal<string | null>(null)
  title = signal<string | null>(null)
  duplicatesCount = signal<number | null>(null)
  options = signal<IndividualToastNotificationConfig<ConfigPayload>>(null)

  originalTimeout: number

  private timeout: NodeJS.Timeout
  private readonly destroyRef$ = inject(DestroyRef)

  private readonly toastService: ToastNotificationService
  private readonly toastPackage: ToastNotificationPackage
  private readonly ngZone: NgZone

  constructor() {
    const toastService = inject(ToastNotificationService)
    const toastPackage = inject(ToastNotificationPackage)
    const ngZone = inject(NgZone)

    this.toastService = toastService
    this.toastPackage = toastPackage
    this.ngZone = ngZone

    this.message.set(this.toastPackage.message)
    this.title.set(this.toastPackage.title)
    this.options.set(this.toastPackage.config)
    this.originalTimeout = this.toastPackage.config.timeOut
    this.toastClasses.set(`${this.toastPackage.toastType} ${this.toastPackage.config.toastClass}`)

    this.setupSubscriptions()

    this.animationState.set({
      value: ToastNotificationStatusValue.INACTIVE,
      params: {
        easeTime: this.toastPackage.config.easeTime,
        easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      },
    })
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeout)
  }

  tapToast(): void {
    if (this.state.value === ToastNotificationStatusValue.REMOVED) {
      return
    }

    this.toastPackage.triggerTap()

    if (this.options().tapToDismiss) {
      this.remove()
    }
  }

  delayedHideToast(): void {
    if (
      this.options().disableTimeOut === true ||
      this.options().disableTimeOut === ToastNotificationDisableTimeOut.EXTENDED_TIME_OUT ||
      this.options().extendedTimeOut === 0 ||
      this.state.value === ToastNotificationStatusValue.REMOVED
    ) {
      return
    }
    this.outsideTimeout(() => this.remove(), this.options().extendedTimeOut)

    this.options.update(options => ({...options, timeOut: this.options().extendedTimeOut}))
  }

  stickAround(): void {
    if (this.state.value === ToastNotificationStatusValue.REMOVED) {
      return
    }

    if (this.options().disableTimeOut !== ToastNotificationDisableTimeOut.EXTENDED_TIME_OUT) {
      clearTimeout(this.timeout)

      this.options.update(options => ({...options, timeOut: 0}))
    }
  }

  /**
   * Activates toast and sets timeout
   */
  activateToast = (): void => {
    this.state = {
      value: ToastNotificationStatusValue.ACTIVE,
      params: {
        easeTime: this.toastPackage.config.easeTime,
        easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      },
    }

    if (
      !(
        this.options().disableTimeOut === true ||
        this.options().disableTimeOut === ToastNotificationDisableTimeOut.TIME_OUT
      ) &&
      this.options().timeOut
    ) {
      this.outsideTimeout(() => this.remove(), this.options().timeOut)
    }
  }

  resetTimeout = (): void => {
    clearTimeout(this.timeout)

    this.state = {
      value: ToastNotificationStatusValue.ACTIVE,
      params: {
        easeTime: this.toastPackage.config.easeTime,
        easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      },
    }

    this.outsideTimeout(() => this.remove(), this.originalTimeout)

    this.options.update(options => ({...options, timeOut: this.originalTimeout}))
  }

  /**
   * Tells toastService to remove this toast after animation time
   */
  remove = (): void => {
    if (this.state.value === ToastNotificationStatusValue.REMOVED) {
      return
    }
    clearTimeout(this.timeout)

    this.state = {
      value: ToastNotificationStatusValue.REMOVED,
      params: {
        easeTime: this.toastPackage.config.easeTime,
        easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
      },
    }

    this.outsideTimeout(() => this.toastService.remove(this.toastPackage.toastId), +this.toastPackage.config.easeTime)
  }

  outsideTimeout = (func: () => any, timeout: number): void => {
    if (this.ngZone) {
      this.ngZone.runOutsideAngular(() => (this.timeout = setTimeout(() => this.runInsideAngular(func), timeout)))
    } else {
      this.timeout = setTimeout(() => func(), timeout)
    }
  }

  onAnimationStart(event: any): void {
    if (event.triggerName !== 'flyInOut') {
      return
    }

    const display = event.toState === ToastNotificationStatusValue.INACTIVE ? 'none' : null
    this.displayStyle.set(display)
  }

  private readonly runInsideAngular = (func: () => any): void => {
    if (this.ngZone) {
      this.ngZone.run(() => func())
    } else {
      func()
    }
  }

  private readonly setupSubscriptions = (): void => {
    this.toastPackage.toastRef
      .afterActivate()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe(() => this.activateToast())

    this.toastPackage.toastRef
      .manualClosed()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe(() => this.remove())

    this.toastPackage.toastRef
      .timeoutReset()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe(() => this.resetTimeout())

    this.toastPackage.toastRef
      .countDuplicate()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe(count => this.duplicatesCount.set(count))
  }
}
